import React, { Suspense, lazy } from "react";
import { Authenticated, Refine } from "@refinedev/core";
import {
  MantineProvider,
  Global,
  ColorSchemeProvider,
  ColorScheme,
  Box,
  Text,
  Title as MantineTitle,
  createStyles,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { NotificationsProvider } from "@mantine/notifications";
import {
  useNotificationProvider,
  ErrorComponent,
  DarkTheme,
  AuthPage,
} from "@refinedev/mantine";

import createDataProvider from "@refinedev/graphql";
import routerProvider, {
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { Sider, Layout, Header } from "components/layout";

import { authProvider, TOKEN_KEY } from "authProvider";
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { Client, fetchExchange } from "@urql/core";
const InternetPackageShow = lazy(() =>
  import("pages/internetPackages/show").then((m) => ({
    default: m.InternetPackageShow,
  })),
);
const InternetPackageList = lazy(() =>
  import("pages/internetPackages/list").then((m) => ({
    default: m.InternetPackageList,
  })),
);
const InternetPackageCreate = lazy(() =>
  import("pages/internetPackages/create").then((m) => ({
    default: m.InternetPackageCreate,
  })),
);
const InternetPackageEdit = lazy(() =>
  import("pages/internetPackages/edit").then((m) => ({
    default: m.InternetPackageEdit,
  })),
);
const AgentAccountList = lazy(() =>
  import("pages/agents/list").then((m) => ({ default: m.AgentAccountList })),
);
const AgentAccountCreate = lazy(() =>
  import("pages/agents/create").then((m) => ({
    default: m.AgentAccountCreate,
  })),
);
const AgentAccountEdit = lazy(() =>
  import("pages/agents/edit").then((m) => ({ default: m.AgentAccountEdit })),
);
const AgentShow = lazy(() =>
  import("pages/agents/show").then((m) => ({ default: m.AgentShow })),
);
const AgentRotationConfig = lazy(() =>
  import("pages/agentRotation/config").then((m) => ({
    default: m.AgentRotationConfig,
  })),
);
const UssdScriptsConfig = lazy(() =>
  import("pages/ussdScripts/config").then((m) => ({
    default: m.UssdScriptsConfig,
  })),
);
const MessageTemplatesConfig = lazy(() =>
  import("pages/messageTemplates/config").then((m) => ({
    default: m.MessageTemplatesConfig,
  })),
);
const AgentAutomationConfig = lazy(() =>
  import("pages/agentAutomation/config").then((m) => ({
    default: m.AgentAutomationConfig,
  })),
);
const UssdCodesList = lazy(() =>
  import("pages/ussdCodes/list").then((m) => ({ default: m.UssdCodesList })),
);
const AccountList = lazy(() =>
  import("pages/accounts/list").then((m) => ({ default: m.AccountList })),
);
const AccountShow = lazy(() =>
  import("pages/accounts/show").then((m) => ({ default: m.AccountShow })),
);
const AccountEdit = lazy(() =>
  import("pages/accounts/edit").then((m) => ({ default: m.AccountEdit })),
);
const ExchangeRateList = lazy(() =>
  import("pages/exchange-rates/list").then((m) => ({
    default: m.ExchangeRateList,
  })),
);
const ExchangeRateShow = lazy(() =>
  import("pages/exchange-rates/show").then((m) => ({
    default: m.ExchangeRateShow,
  })),
);
const ExchangeRateEdit = lazy(() =>
  import("pages/exchange-rates/edit").then((m) => ({
    default: m.ExchangeRateEdit,
  })),
);
const ExchangeRateCreate = lazy(() =>
  import("pages/exchange-rates/create").then((m) => ({
    default: m.ExchangeRateCreate,
  })),
);
const WalletList = lazy(() =>
  import("pages/wallets/list").then((m) => ({ default: m.WalletList })),
);
const TransactionsShow = lazy(() =>
  import("pages/transactions/show").then((m) => ({
    default: m.TransactionsShow,
  })),
);
const TransactionList = lazy(() =>
  import("pages/transactions/list").then((m) => ({
    default: m.TransactionList,
  })),
);
const SplitBillList = lazy(() =>
  import("pages/splitBills/list").then((m) => ({ default: m.SplitBillList })),
);
const SplitBillShow = lazy(() =>
  import("pages/splitBills/show").then((m) => ({ default: m.SplitBillShow })),
);
const TransactionEdit = lazy(() =>
  import("pages/transactions/edit").then((m) => ({
    default: m.TransactionEdit,
  })),
);
const SmsList = lazy(() =>
  import("pages/sms/list").then((m) => ({ default: m.SmsList })),
);
const ProviderPreferenceList = lazy(() =>
  import("pages/providerPrefernece/list").then((m) => ({
    default: m.ProviderPreferenceList,
  })),
);
const ProviderPreferenceEdit = lazy(() =>
  import("pages/providerPrefernece/edit").then((m) => ({
    default: m.ProviderPreferenceEdit,
  })),
);
const ProviderPreferenceCreate = lazy(() =>
  import("pages/providerPrefernece/create").then((m) => ({
    default: m.ProviderPreferenceCreate,
  })),
);
const TransactionCreate = lazy(() =>
  import("pages/transactions/create").then((m) => ({
    default: m.TransactionCreate,
  })),
);
const CreditVoucherList = lazy(() =>
  import("pages/credits/credit-vouchers/list").then((m) => ({
    default: m.CreditVoucherList,
  })),
);
const CreditVoucherCreate = lazy(() =>
  import("pages/credits/credit-vouchers/create").then((m) => ({
    default: m.CreditVoucherCreate,
  })),
);
const CreditVoucherEdit = lazy(() =>
  import("pages/credits/credit-vouchers/edit").then((m) => ({
    default: m.CreditVoucherEdit,
  })),
);
const CreditVoucherShow = lazy(() =>
  import("pages/credits/credit-vouchers/show").then((m) => ({
    default: m.CreditVoucherShow,
  })),
);
const CreditWalletList = lazy(() =>
  import("pages/credits/credit-wallets/list").then((m) => ({
    default: m.CreditWalletList,
  })),
);
const CreditWalletShow = lazy(() =>
  import("pages/credits/credit-wallets/show").then((m) => ({
    default: m.CreditWalletShow,
  })),
);
const CreditWalletHistoryList = lazy(() =>
  import("pages/credits/credit-wallet-histories/list").then((m) => ({
    default: m.CreditWalletHistoryList,
  })),
);
const CreditVoucherClaimList = lazy(() =>
  import("pages/credits/credit-voucher-claims/list").then((m) => ({
    default: m.CreditVoucherClaimList,
  })),
);
const CreditTransferPage = lazy(() =>
  import("pages/credits/credit-transfer").then((m) => ({
    default: m.CreditTransferPage,
  })),
);
const AnalyticsPage = lazy(() =>
  import("pages/analytics").then((m) => ({
    default: m.AnalyticsPage,
  })),
);
const PushNotificationSend = lazy(() =>
  import("pages/notifications/push").then((m) => ({
    default: m.PushNotificationSend,
  })),
);
const SupportConsole = lazy(() =>
  import("pages/support/console").then((m) => ({ default: m.SupportConsole })),
);
const SupportTemplates = lazy(() =>
  import("pages/support/templates").then((m) => ({
    default: m.SupportTemplatesPage,
  })),
);
const SupportAutoMessages = lazy(() =>
  import("pages/support/auto-messages").then((m) => ({
    default: m.SupportAutoMessagesConfig,
  })),
);
const SupportScheduledMessages = lazy(() =>
  import("pages/support/scheduled-messages").then((m) => ({
    default: m.SupportScheduledMessagesPage,
  })),
);

const API_URL =
  process.env.REACT_APP_API_URL ?? "http://localhost:8000/graphql";

const buildWhere = (filters?: any[]) => {
  if (!Array.isArray(filters)) {
    return undefined;
  }

  const where = filters.reduce((acc: Record<string, unknown>, filter) => {
    if (!("field" in filter)) {
      return acc;
    }

    const value = filter.value;

    if (value === undefined || value === null || value === "") {
      return acc;
    }

    acc[filter.field] = value;

    return acc;
  }, {});

  return Object.keys(where).length > 0 ? where : undefined;
};

const buildSort = (sorters?: any[]) => {
  if (!Array.isArray(sorters) || sorters.length === 0) {
    return undefined;
  }

  const sorter = sorters[0];

  if (!sorter?.field || !sorter?.order) {
    return undefined;
  }

  return `${sorter.field}:${sorter.order}`;
};

const client = new Client({
  url: API_URL,
  exchanges: [fetchExchange],
  fetchOptions: () => ({
    headers: {
      authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) ?? ""}`,
    },
  }),
});

const gqlDataProvider = createDataProvider(client, {
  create: {
    buildVariables: (params) => ({
      input: {
        data: params.variables ?? params.meta?.gqlVariables,
        ...params.meta?.gqlVariables,
      },
    }),
  },
  getList: {
    buildVariables: (params) => {
      const pagination = params.pagination as
        { currentPage?: number; pageSize?: number } | undefined;
      const currentPage = pagination?.currentPage ?? 1;
      const pageSize = pagination?.pageSize ?? 10;

      return {
        start: (currentPage - 1) * pageSize,
        limit: pageSize,
        where: buildWhere(params.filters),
        sort: buildSort(params.sorters),
        ...params.meta?.variables,
        ...params.meta?.gqlVariables,
      };
    },
    dataMapper: (response, params) => {
      const data = response.data?.[params.resource];

      if (Array.isArray(data)) {
        return data;
      }

      return data?.nodes ?? [];
    },
    getTotalCount: (response, params) => {
      const data = response.data?.[params.resource];

      if (!Array.isArray(data)) {
        return data?.totalCount ?? 0;
      }

      const pagination = params.pagination as
        { currentPage?: number; pageSize?: number } | undefined;
      const currentPage = pagination?.currentPage ?? 1;
      const pageSize = pagination?.pageSize ?? data.length;
      const offset = (currentPage - 1) * pageSize;

      if (data.length === 0) {
        return offset;
      }

      if (data.length < pageSize) {
        return offset + data.length;
      }

      return currentPage * pageSize + 1;
    },
  },
  update: {
    buildVariables: (params) => ({
      input: {
        where: { id: params.id },
        data: params.variables,
        ...params.meta?.gqlVariables,
      },
    }),
  },
});

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) ?? ""}`,
    },
  }));

  return forward(operation);
});

const httpLink = new HttpLink({
  uri: API_URL,
});

const apolloClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from([authLink, httpLink]),
});

const useLoginStyles = createStyles((theme) => ({
  shell: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing.xl,
    background:
      theme.colorScheme === "dark"
        ? "radial-gradient(circle at top, rgba(90, 103, 216, 0.22), transparent 36%), linear-gradient(180deg, #081121 0%, #0d1728 50%, #060b13 100%)"
        : "radial-gradient(circle at top, rgba(79, 70, 229, 0.12), transparent 32%), linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)",
  },
  card: {
    width: "min(100%, 460px)",
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing.xs,
    padding: "6px 12px",
    borderRadius: 999,
    marginBottom: theme.spacing.md,
    color: theme.colorScheme === "dark" ? theme.white : theme.colors.dark[7],
    backgroundColor:
      theme.colorScheme === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(79, 70, 229, 0.08)",
    border: `1px solid ${
      theme.colorScheme === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(79, 70, 229, 0.12)"
    }`,
  },
}));

const LoginPage: React.FC = () => {
  const { classes } = useLoginStyles();

  return (
    <Box className={classes.shell}>
      <Box className={classes.card}>
        <Box mb="lg">
          <Text className={classes.eyebrow}>Ebixi Admin Console</Text>
          <MantineTitle order={1} mb="xs">
            Operations hub for teams and wallets
          </MantineTitle>
          <Text color="dimmed">
            Sign in to manage accounts, transactions, providers, credits, and
            exchange rates from one place.
          </Text>
        </Box>
        <AuthPage
          formProps={{
            validate: {},
          }}
        />
      </Box>
    </Box>
  );
};

function App() {
  const [colorScheme, setColorScheme] = useLocalStorage<ColorScheme>({
    key: "mantine-color-scheme",
    defaultValue: "dark",
    getInitialValueInEffect: true,
  });

  const toggleColorScheme = (value?: ColorScheme) =>
    setColorScheme(value || (colorScheme === "dark" ? "light" : "dark"));

  const notificationProvider = useNotificationProvider();

  return (
    <BrowserRouter>
      <ApolloProvider client={apolloClient}>
        <ColorSchemeProvider
          colorScheme={colorScheme}
          toggleColorScheme={toggleColorScheme}
        >
          <MantineProvider
            theme={{
              ...DarkTheme,
              colorScheme,
              primaryColor: "violet",
              primaryShade: { dark: 4, light: 6 },
              fontFamily:
                'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              headings: {
                fontFamily:
                  'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              },
              colors: {
                ...DarkTheme.colors,
                brand: [
                  "#eef2ff",
                  "#e0e7ff",
                  "#c7d2fe",
                  "#a5b4fc",
                  "#818cf8",
                  "#6366f1",
                  "#4f46e5",
                  "#4338ca",
                  "#3730a3",
                  "#312e81",
                ],
              },
              defaultRadius: "md",
              components: {
                Card: {
                  defaultProps: {
                    radius: "lg",
                    shadow: "sm",
                    withBorder: true,
                    p: "lg",
                  },
                },
                Paper: {
                  defaultProps: {
                    radius: "lg",
                    shadow: "sm",
                    withBorder: true,
                  },
                },
                Button: {
                  defaultProps: {
                    radius: "md",
                  },
                },
                TextInput: {
                  defaultProps: {
                    radius: "md",
                    size: "md",
                  },
                },
                Select: {
                  defaultProps: {
                    radius: "md",
                    size: "md",
                  },
                },
                NumberInput: {
                  defaultProps: {
                    radius: "md",
                    size: "md",
                  },
                },
                Table: {
                  defaultProps: {
                    highlightOnHover: true,
                    striped: true,
                    verticalSpacing: "md",
                    horizontalSpacing: "md",
                    fontSize: "sm",
                  },
                },
              },
            }}
            withNormalizeCSS
            withGlobalStyles
          >
            <Global
              styles={(theme) => ({
                body: {
                  WebkitFontSmoothing: "auto",
                  background:
                    theme.colorScheme === "dark"
                      ? "linear-gradient(180deg, #07111f 0%, #0c1624 100%)"
                      : "linear-gradient(180deg, #f8fbff 0%, #edf3ff 100%)",
                },
                "#root": {
                  minHeight: "100vh",
                },
                "*::selection": {
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? "rgba(99, 102, 241, 0.45)"
                      : "rgba(79, 70, 229, 0.18)",
                },
                "@media (max-width: 48em)": {
                  ".admin-responsive-table": {
                    minWidth: "100% !important",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  },
                  ".admin-responsive-table thead": {
                    display: "none",
                  },
                  ".admin-responsive-table tbody": {
                    display: "block",
                  },
                  ".admin-responsive-table tr": {
                    display: "block",
                    marginBottom: theme.spacing.sm,
                    padding: theme.spacing.xs,
                    border: `1px solid ${
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[4]
                        : theme.colors.gray[2]
                    }`,
                    borderRadius: theme.radius.md,
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[6]
                        : theme.white,
                  },
                  ".admin-responsive-table tr:last-of-type": {
                    marginBottom: 0,
                  },
                  ".admin-responsive-table td": {
                    display: "grid",
                    gridTemplateColumns: "minmax(7rem, 38%) minmax(0, 1fr)",
                    gap: theme.spacing.sm,
                    alignItems: "start",
                    width: "100% !important",
                    padding: `${theme.spacing.xs}px 0`,
                    borderTop: `1px solid ${
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[4]
                        : theme.colors.gray[2]
                    }`,
                    whiteSpace: "normal",
                  },
                  ".admin-responsive-table td:first-of-type": {
                    borderTop: 0,
                  },
                  ".admin-responsive-table td::before": {
                    content: "attr(data-label)",
                    color:
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[1]
                        : theme.colors.gray[6],
                    fontWeight: 700,
                    overflowWrap: "anywhere",
                  },
                  ".admin-responsive-table td.admin-responsive-table-empty": {
                    display: "block",
                    padding: `${theme.spacing.md}px 0`,
                  },
                  ".admin-responsive-table td.admin-responsive-table-empty::before":
                    {
                      content: "none",
                    },
                  ".admin-responsive-table td[data-label='Action']": {
                    display: "block",
                  },
                  ".admin-responsive-table td[data-label='Action']::before": {
                    display: "block",
                    marginBottom: theme.spacing.xs,
                  },
                  ".admin-responsive-table .admin-responsive-actions": {
                    flexDirection: "column",
                    flexWrap: "nowrap",
                    alignItems: "stretch",
                    gap: theme.spacing.sm,
                  },
                  ".admin-responsive-table .admin-responsive-actions > *": {
                    maxWidth: "100%",
                  },
                  ".admin-responsive-table .admin-responsive-actions .mantine-Button-root":
                    {
                      width: "100%",
                      minHeight: 40,
                      justifyContent: "center",
                    },
                  ".admin-responsive-table .admin-responsive-actions .mantine-ActionIcon-root":
                    {
                      width: "100%",
                      minHeight: 40,
                    },
                  ".admin-responsive-table td > *": {
                    minWidth: 0,
                    maxWidth: "100%",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                  },
                },
              })}
            />
            <NotificationsProvider position="top-right">
              <Refine
                options={{
                  disableTelemetry: true,
                }}
                dataProvider={gqlDataProvider}
                notificationProvider={notificationProvider}
                routerProvider={routerProvider}
                authProvider={authProvider}
                resources={[
                  {
                    name: "analytics",
                    list: "/analytics",
                    meta: { label: "Analytics" },
                  },

                  {
                    name: "transactions",
                    list: "/transactions",
                    show: "/transactions/:id",
                    edit: "/transactions/:id/edit",
                    create: "/transactions/create",
                  },

                  {
                    // Must match the list query's field name: the data
                    // provider reads response.data[resource].
                    name: "adminSplitBills",
                    list: "/split-bills",
                    show: "/split-bills/:id",
                    meta: { label: "Split bills" },
                  },

                  {
                    name: "adminInternetPackages",
                    list: "/internet-packages",
                    show: "/internet-packages/:id",
                    create: "/internet-packages/create",
                    edit: "/internet-packages/:id/edit",
                  },

                  {
                    name: "accounts",
                    list: "/accounts",
                    show: "/accounts/:id",
                    edit: "/accounts/:id/edit",
                  },

                  {
                    name: "exchangeRates",
                    list: "/exchange-rates",
                    show: "/exchange-rates/:id",
                    edit: "/exchange-rates/:id/edit",
                    create: "/exchange-rates/create",
                  },

                  {
                    name: "credits",
                    list: "/credits",
                  },
                  {
                    name: "credit-wallets",
                    meta: { parent: "credits", label: "Wallets" },
                    list: "/credits/wallets",
                    show: "/credits/wallets/:id",
                  },
                  {
                    name: "credit-wallet-histories",
                    meta: { parent: "credits", label: "Wallet History" },
                    list: "/credits/wallet-histories",
                  },
                  {
                    name: "credit-voucher-claims",
                    meta: { parent: "credits", label: "Voucher Claims" },
                    list: "/credits/voucher-claims",
                  },

                  {
                    name: "credit-vouchers",
                    meta: { parent: "credits", label: "Vouchers" },
                    list: "/credits/vouchers",
                    create: "/credits/vouchers/create",
                    show: "/credits/vouchers/:id",
                    edit: "/credits/vouchers/:id/edit",
                  },
                  {
                    name: "credit-transfer",
                    meta: { parent: "credits", label: "Transfer" },
                    list: "/credits/transfer",
                  },
                  {
                    name: "sms",
                    list: "/sms",
                  },
                  {
                    name: "agentAccounts",
                    list: "/agents",
                    create: "/agents/create",
                    edit: "/agents/:id/edit",
                    show: "/agents/:id",
                  },
                  {
                    name: "agentRotation",
                    list: "/agent-rotation",
                    meta: { label: "Agent Rotation" },
                  },
                  {
                    name: "agentAutomation",
                    list: "/agent-automation",
                    meta: { label: "Agent Automation" },
                  },
                  {
                    name: "ussdScripts",
                    list: "/ussd-scripts",
                    meta: { label: "USSD Scripts", parent: "agentAutomation" },
                  },
                  {
                    // Grouped with the other provider-behaviour config, which is
                    // where an operator goes when a provider changes a message.
                    name: "messageTemplates",
                    list: "/message-templates",
                    meta: {
                      label: "Message Templates",
                      parent: "agentAutomation",
                    },
                  },
                  {
                    name: "ussdCodes",
                    list: "/ussd-codes",
                    meta: {
                      label: "USSD Dial Codes",
                      parent: "agentAutomation",
                    },
                  },
                  {
                    name: "providerPreferences",
                    list: "/provider-preferences",
                    create: "/provider-preferences/create",
                    edit: "/provider-preferences/:id/edit",
                  },
                  {
                    name: "wallets",
                    list: "/wallets",
                  },
                  {
                    name: "pushNotifications",
                    list: "/push-notifications",
                    meta: { label: "Push Notifications" },
                  },
                  {
                    name: "support",
                    list: "/support",
                    meta: { label: "Support" },
                  },
                  {
                    name: "supportChat",
                    list: "/support/chat",
                    meta: { label: "Support Chat", parent: "support" },
                  },
                  {
                    name: "supportTemplates",
                    list: "/support/templates",
                    meta: {
                      label: "Message Templates",
                      parent: "support",
                    },
                  },
                  {
                    name: "supportAutoMessages",
                    list: "/support/auto-messages",
                    meta: {
                      label: "Automatic Messages",
                      parent: "support",
                    },
                  },
                  {
                    name: "supportScheduledMessages",
                    list: "/support/scheduled-messages",
                    meta: {
                      label: "Scheduled Announcements",
                      parent: "support",
                    },
                  },
                ]}
              >
                <Suspense
                  fallback={
                    <Box p="xl">
                      <Text color="dimmed">Loading page...</Text>
                    </Box>
                  }
                >
                  <Routes>
                    <Route
                      element={
                        <Authenticated
                          key="protected-routes"
                          fallback={<Navigate to="/login" replace />}
                        >
                          <Outlet />
                        </Authenticated>
                      }
                    >
                      <Route
                        element={
                          <Layout Header={Header} Sider={Sider}>
                            <Outlet />
                          </Layout>
                        }
                      >
                        <Route
                          index
                          element={
                            <NavigateToResource resource="transactions" />
                          }
                        />

                        <Route path="/analytics" element={<AnalyticsPage />} />

                        <Route
                          path="/transactions"
                          element={<TransactionList />}
                        />
                        <Route
                          path="/transactions/create"
                          element={<TransactionCreate />}
                        />
                        <Route
                          path="/transactions/:id"
                          element={<TransactionsShow />}
                        />
                        <Route
                          path="/transactions/:id/edit"
                          element={<TransactionEdit />}
                        />

                        <Route
                          path="/split-bills"
                          element={<SplitBillList />}
                        />
                        <Route
                          path="/split-bills/:id"
                          element={<SplitBillShow />}
                        />

                        <Route
                          path="/internet-packages"
                          element={<InternetPackageList />}
                        />
                        <Route
                          path="/internet-packages/create"
                          element={<InternetPackageCreate />}
                        />
                        <Route
                          path="/internet-packages/:id"
                          element={<InternetPackageShow />}
                        />
                        <Route
                          path="/internet-packages/:id/edit"
                          element={<InternetPackageEdit />}
                        />

                        <Route path="/accounts" element={<AccountList />} />
                        <Route path="/accounts/:id" element={<AccountShow />} />
                        <Route
                          path="/accounts/:id/edit"
                          element={<AccountEdit />}
                        />

                        <Route
                          path="/exchange-rates"
                          element={<ExchangeRateList />}
                        />
                        <Route
                          path="/exchange-rates/create"
                          element={<ExchangeRateCreate />}
                        />
                        <Route
                          path="/exchange-rates/:id"
                          element={<ExchangeRateShow />}
                        />
                        <Route
                          path="/exchange-rates/:id/edit"
                          element={<ExchangeRateEdit />}
                        />

                        <Route
                          path="/credits"
                          element={
                            <NavigateToResource resource="credit-wallets" />
                          }
                        />
                        <Route
                          path="/credits/wallets"
                          element={<CreditWalletList />}
                        />
                        <Route
                          path="/credits/wallets/:id"
                          element={<CreditWalletShow />}
                        />
                        <Route
                          path="/credits/wallet-histories"
                          element={<CreditWalletHistoryList />}
                        />
                        <Route
                          path="/credits/voucher-claims"
                          element={<CreditVoucherClaimList />}
                        />
                        <Route
                          path="/credits/vouchers"
                          element={<CreditVoucherList />}
                        />
                        <Route
                          path="/credits/vouchers/create"
                          element={<CreditVoucherCreate />}
                        />
                        <Route
                          path="/credits/vouchers/:id"
                          element={<CreditVoucherShow />}
                        />
                        <Route
                          path="/credits/vouchers/:id/edit"
                          element={<CreditVoucherEdit />}
                        />
                        <Route
                          path="/credits/transfer"
                          element={<CreditTransferPage />}
                        />

                        <Route path="/sms" element={<SmsList />} />

                        <Route path="/agents" element={<AgentAccountList />} />
                        <Route
                          path="/agents/create"
                          element={<AgentAccountCreate />}
                        />
                        <Route path="/agents/:id" element={<AgentShow />} />
                        <Route
                          path="/agents/:id/edit"
                          element={<AgentAccountEdit />}
                        />

                        <Route
                          path="/agent-rotation"
                          element={<AgentRotationConfig />}
                        />

                        <Route
                          path="/agent-automation"
                          element={<AgentAutomationConfig />}
                        />
                        <Route
                          path="/ussd-scripts"
                          element={<UssdScriptsConfig />}
                        />
                        <Route
                          path="/message-templates"
                          element={<MessageTemplatesConfig />}
                        />
                        <Route path="/ussd-codes" element={<UssdCodesList />} />

                        <Route
                          path="/provider-preferences"
                          element={<ProviderPreferenceList />}
                        />
                        <Route
                          path="/provider-preferences/create"
                          element={<ProviderPreferenceCreate />}
                        />
                        <Route
                          path="/provider-preferences/:id/edit"
                          element={<ProviderPreferenceEdit />}
                        />

                        <Route path="/wallets" element={<WalletList />} />

                        <Route
                          path="/push-notifications"
                          element={<PushNotificationSend />}
                        />

                        <Route
                          path="/support/chat"
                          element={<SupportConsole />}
                        />
                        <Route
                          path="/support/templates"
                          element={<SupportTemplates />}
                        />
                        <Route
                          path="/support/auto-messages"
                          element={<SupportAutoMessages />}
                        />
                        <Route
                          path="/support/scheduled-messages"
                          element={<SupportScheduledMessages />}
                        />

                        <Route path="*" element={<ErrorComponent />} />
                      </Route>
                    </Route>

                    <Route
                      element={
                        <Authenticated key="auth-routes" fallback={<Outlet />}>
                          <NavigateToResource resource="transactions" />
                        </Authenticated>
                      }
                    >
                      <Route path="/login" element={<LoginPage />} />
                    </Route>
                  </Routes>
                </Suspense>
                <UnsavedChangesNotifier />
              </Refine>
            </NotificationsProvider>
          </MantineProvider>
        </ColorSchemeProvider>
      </ApolloProvider>
    </BrowserRouter>
  );
}

export default App;
